import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { metaByIdFromDefs } from "../../helpers/scoreAggregate";
import { UseCompanySubWorkspaces, UseCompanyWorkspaces, UseUsersFullData } from "../../hooks/company";
import {
  fetchWorkspaceScores,
  monthsForWindow,
  resolveWindow,
  sumScores,
} from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";

/**
 * Labor cost against revenue, a row per department — the payroll sheet, live.
 *
 * What is typed and what is worked out, exactly as the sheet has it:
 *
 *   hours        ← the department's own metric (HCP feeds the technicians')
 *   revenue      ← the company's Total Revenue metric when one exists, else typed
 *   goal %       ← typed
 *   employee pay ← typed per employee, on the card itself (click a Pay/hour cell)
 *
 *   pay/hour     = average of that department's employees' own pay/hour
 *   total cost   = hours x pay/hour, once a pay/hour exists — typed as a
 *                  fallback (Setup's "Total cost") for a department with no
 *                  employee rates entered yet
 *   performance  = total cost / revenue
 *   goal $       = goal % x revenue
 *   over/under   = goal % - performance     (positive = under = good)
 *   over/under $ = total cost - goal $      (positive = over  = bad)
 *
 * The two over/under columns carry opposite signs — that is the sheet, not a
 * slip. Both are shown as a magnitude and coloured from one `over` flag, so a
 * department never reads red in one column and green in the next.
 */

const rid = () => `r${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

/* A row is one workspace, or several combined into one line — a single
   workspace still names the department (the common case, unchanged); two or
   more combined (an "Others" catch-all: whichever small workspaces don't
   rate their own row) have no one natural name, so a combined row carries
   its own typed `label` instead. */
export const emptyRow = () => ({
  key: rid(),
  workspaceId: "",
  workspaceIds: [],
  hoursKpiId: "",
  label: "",
  hours: "",
  totalCost: "",
  goalPct: "",
  includeSubWorkspaces: true,
});

/** Department rows as stored on the widget. */
export const rowsOf = (s) => (Array.isArray(s?.rows) ? s.rows.filter(Boolean) : []);

/* The row's member workspaces, in order — `workspaceIds` once a row has been
   through the multi-pick, falling back to the single legacy `workspaceId`
   for every row saved before that existed. Always the source of truth for
   "what does this row count", in Setup and on the board alike. */
export const workspaceIdsOf = (row) => {
  const ids = Array.isArray(row?.workspaceIds) ? row.workspaceIds.filter(Boolean).map(String) : [];
  if (ids.length) return ids;
  return row?.workspaceId ? [String(row.workspaceId)] : [];
};

/** Two or more workspaces combined into this one line. */
export const isCombinedRow = (row) => workspaceIdsOf(row).length > 1;

/* Undefined reads as "on" — every row before this setting existed already
   rolled its sub-workspaces up, so an old save can't silently change what a
   department counts. */
export const includesSubWorkspaces = (row) => row?.includeSubWorkspaces !== false;

/** Per-employee pay rates on a row, keyed by userId. */
export const employeePayOf = (row) => (row?.employeePay && typeof row.employeePay === "object" ? row.employeePay : {});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* ── Monthly-rate pay ──────────────────────────────────────────────────────
   A pay entry is typed on the card per employee (see index.jsx): a plain
   number is an hourly rate, exactly as it always was. `{ mode: "monthly",
   value }` is a monthly salary instead — someone paid the same amount every
   month regardless of how many hours or weekdays it happens to have. Total
   cost still has to come out of hours × pay/hour (that's every other figure
   on this card), so a monthly rate is converted to its hourly-equivalent:
   split evenly across the weekdays in whichever month(s) the board window
   touches (each month's own count — a 20-weekday February is not a
   23-weekday March), assuming an 8-hour workday. Getting that weekday count
   wrong in either direction is exactly the kind of silent error that looks
   fine until payday, so this is its own well-tested seam rather than folded
   into the hook below. */
export const HOURLY_STANDARD_DAY = 8;

/** Mon–Fri days across every "YYYY-MM" in `months` — summed per month, never
    approximated with one average-days-a-month constant. */
export const totalWeekdaysIn = (months) => (Array.isArray(months) ? months : []).reduce((sum, monthKey) => {
  const [y, m] = String(monthKey).split("-").map(Number);
  if (!y || !m) return sum;
  const daysInMonth = new Date(y, m, 0).getDate();
  let weekdays = 0;
  for (let d = 0; d < daysInMonth; d += 1) {
    const dow = new Date(y, m - 1, d + 1).getDay();
    if (dow > 0 && dow < 6) weekdays += 1;
  }
  return sum + weekdays;
}, 0);

/** A monthly salary's hourly-equivalent over `months` (one or more "YYYY-MM"
    strings) — null when there's nothing to divide by yet. */
export const monthlyToHourly = (monthlyValue, months) => {
  const val = Number(monthlyValue);
  if (!(val > 0) || !Array.isArray(months) || !months.length) return null;
  const weekdays = totalWeekdaysIn(months);
  if (!weekdays) return null;
  return (val * months.length) / (weekdays * HOURLY_STANDARD_DAY);
};

/** Mon–Fri days across `months` that have actually happened by `today` — a
    past month counts in full, the current month only up to (and including)
    today, a future month not at all. Display hours only: a salary's rate and
    cost still rest on the full month (totalWeekdaysIn). */
export const weekdaysElapsedIn = (months, today = new Date()) => (Array.isArray(months) ? months : []).reduce((sum, monthKey) => {
  const [y, m] = String(monthKey).split("-").map(Number);
  if (!y || !m) return sum;
  const daysInMonth = new Date(y, m, 0).getDate();
  const cur = today.getFullYear() * 12 + today.getMonth();
  const mon = y * 12 + (m - 1);
  let lastDay = 0;
  if (mon < cur) lastDay = daysInMonth;
  else if (mon === cur) lastDay = Math.min(today.getDate(), daysInMonth);
  let weekdays = 0;
  for (let d = 1; d <= lastDay; d += 1) {
    const dow = new Date(y, m - 1, d).getDay();
    if (dow > 0 && dow < 6) weekdays += 1;
  }
  return sum + weekdays;
}, 0);

/** The same 8hr/weekday assumption `monthlyToHourly` divides a salary by,
    surfaced as an actual hours figure — elapsed days only, so mid-month it
    reads as hours so far, not the whole month's — for a salaried employee with no live
    Hours Worked feed, this is the only number the Hours column can show that
    isn't simply invented, since it's the exact assumption their Pay/hour
    figure already rests on. null with nothing to count (no months resolved
    yet). Never used for cost — monthlyCostOf accrues the salary by calendar,
    not by hours; this is display (and the department Hours total) only. */
export const monthlyAssumedHours = (months, today = new Date()) => {
  const weekdays = weekdaysElapsedIn(months, today);
  return weekdays > 0 ? weekdays * HOURLY_STANDARD_DAY : null;
};

/** "hourly" (a plain number, or nothing typed yet) or "monthly" ({ mode }). */
export const payEntryMode = (entry) => (entry && typeof entry === "object" && entry.mode === "monthly" ? "monthly" : "hourly");

/** The typed figure, whatever the entry's mode — "" once nothing is typed, so
    a number input can bind to it directly. */
export const payEntryValue = (entry) => {
  if (entry == null) return "";
  if (typeof entry === "object") return entry.mode === "monthly" && entry.value != null ? entry.value : "";
  return entry;
};

/** The figure this card actually multiplies hours by — an hourly entry as
    typed, a monthly one converted via monthlyToHourly, null for neither. */
export const hourlyRateOf = (entry, months) => {
  if (entry == null) return null;
  if (typeof entry === "object") return entry.mode === "monthly" ? monthlyToHourly(entry.value, months) : null;
  const n = Number(entry);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** What a monthly entry has actually cost so far in this window, in real
    dollars — the salary times how much of each month has elapsed (a past
    month in full, the current month by weekdays gone / weekdays in the month,
    a future month not at all). A salary accrues with the calendar, not with
    hours: it is owed whether or not the person logged a single hour (that is
    the whole point of paying someone monthly), so this is deliberately NOT
    hours × hourlyRateOf. Charging the WHOLE month on day 5 was what pinned
    Payroll performance over 100% from the first of the month, against revenue
    that had only 5 days in it. null for an hourly entry, or nothing typed. */
export const monthlyCostOf = (entry, months, today = new Date()) => {
  if (!entry || typeof entry !== "object" || entry.mode !== "monthly") return null;
  const val = Number(entry.value);
  if (!(val > 0) || !Array.isArray(months) || !months.length) return null;
  const share = months.reduce((sum, monthKey) => {
    const total = totalWeekdaysIn([monthKey]);
    return total > 0 ? sum + weekdaysElapsedIn([monthKey], today) / total : sum;
  }, 0);
  return val * share;
};

/* A workspace's real Hours Worked metric — the one flag Scorekeeper's own
   Hours Worked handling checks (hooks/kpi.js), not a name match. Shared with
   LaborSetup so "which KPI counts as hours" is answered in exactly one place. */
export const hoursWorkedKpiOf = (companyKpis, wsId) => {
  if (!wsId) return null;
  const hit = (companyKpis?.[String(wsId)] || []).find(
    (k) => k && !k.isDraft && !k.disabled && (k.type === "hours_worked" || k?.model?.type === "hours_worked"),
  );
  return hit ? String(hit._id) : null;
};

/* Real roster entries only — no admins, no the Yeti bot. Unlike the
   leaderboard's isBoardPlayer, archived/deactivated accounts are NOT excluded
   here on purpose: a technician archived mid-period still has real hours
   worked before that, and payroll has to account for every hour paid out,
   not just whoever is still active today. Whether an archived (or simply
   hours-less) person actually gets a row is decided below, per workspace, by
   whether they have Hours Worked to show — not by this. */
export const employeeName = (u) => {
  const full = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  return full || String(u?.name || u?.email || "").trim();
};
/* Admin used to be excluded outright, on the assumption that an admin
   account was never a real paid employee — wrong for a company whose
   President or Production Manager genuinely draws a paycheck and has to
   show up in payroll totals like anyone else. Only the Yeti bot account is
   excluded now; any admin with a real name counts as a real employee here. */
export const isRealEmployee = (u) => {
  if (!u) return false;
  if (u.lastName === "Yeti" || u.isYeti) return false;
  return !!employeeName(u);
};

/** The typed fallback figure — used only when no live Total Revenue metric is found. */
export const revenueOf = (s) => num(s?.revenue);

/** Where Setup's self-heal last pointed the live Total Revenue metric, if any. */
export const revenueKpiOf = (s) =>
  s?.revenueWorkspaceId && s?.revenueKpiId
    ? { workspaceId: String(s.revenueWorkspaceId), kpiId: String(s.revenueKpiId) }
    : null;

/** "26%", "26.4%" — a decimal only when it carries information. */
export const pct = (value) => {
  const n = num(value);
  const rounded = Math.round(n * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

export const money = (value) => `$${Math.round(num(value)).toLocaleString()}`;

/** A pay rate is the one figure here worth cents. */
export const rate = (value) =>
  `$${num(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Hours read as "1,000" or "37.5" — never "37.50". */
export const hoursLabel = (value) => {
  const n = Math.round(num(value) * 10) / 10;
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

/**
 * @param {object} settings  the widget's saved options
 * @param {string} bucket    today | week | month | lastMonth | quarter | ytd | custom
 * @param {object} [custom]  { from, to } when bucket is "custom"
 */
export default function useLaborData(settings, bucket = "month", custom = null) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const allUsers = UseUsersFullData();
  const subWsByParent = UseCompanySubWorkspaces();
  const workspaces = UseCompanyWorkspaces(true) || [];

  const rows = rowsOf(settings);
  const typedRevenue = revenueOf(settings);
  const revKpi = revenueKpiOf(settings);

  const wsNameById = useMemo(() => {
    const m = {};
    workspaces.forEach((w) => { m[String(w._id)] = w.name; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(workspaces.map((w) => [w._id, w.name]))]);

  /* A department's roster: every workspace the row combines (almost always
     one; two or more once a row has been through "combine workspaces" —
     e.g. an "Others" row lumping together whatever doesn't rate its own
     line), plus each one's own sub-workspaces (Sales → Field Sales,
     In-House Sales…) — a group workspace's own users virtual is empty; the
     people are on the children. Each employee row remembers which
     workspace it came from so a combined or grouped department can still
     show it. A row can turn sub-workspaces off (includesSubWorkspaces) to
     count only its own member workspaces — e.g. a department that groups a
     sibling team for other purposes but shouldn't pull that team's payroll
     into this one. */
  const memberWsOf = (wsIdList, includeSubs = true) => wsIdList.flatMap((wsId) => [
    { id: String(wsId), name: null, sub: false },
    ...(includeSubs
      ? (subWsByParent?.[String(wsId)] || []).map((w) => ({ id: String(w._id), name: w.name, sub: true }))
      : []),
  ]);

  /* Only workspaces we actually read live figures from — a row with typed
     hours, or a company with no live Total Revenue metric, costs no fetch.
     Every member is resolved fresh against companyKpis rather than trusting
     a row's saved hoursKpiId — that field only self-heals while Setup is
     open (LaborSetup's own effect), so a metric swapped, disabled or
     recreated since a row was last edited in Setup would otherwise keep
     reading a defunct kpiId on the live board forever. Sub-workspaces are
     included whenever THEY track Hours Worked, even if their parent's own
     row is typed — that's what puts a real number on an employee row under
     a group department — unless the row has excluded sub-workspaces. */
  const wsIds = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      memberWsOf(workspaceIdsOf(r), includesSubWorkspaces(r)).forEach((mw) => {
        if (hoursWorkedKpiOf(companyKpis, mw.id)) set.add(mw.id);
      });
    });
    if (revKpi) set.add(revKpi.workspaceId);
    return [...set];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(rows.map((r) => [workspaceIdsOf(r), includesSubWorkspaces(r)])),
    revKpi?.workspaceId,
    revKpi?.kpiId,
    subWsByParent,
    companyKpis,
  ]);

  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [scoresByWs, setScoresByWs] = useState({});
  const [loadedKey, setLoadedKey] = useState("");

  const months = useMemo(
    () => monthsForWindow(bucket === "year" ? "ytd" : bucket, custom),
    [bucket, customKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const fetchKey = `${companyId || ""}|${wsIds.join(",")}|${months.join(",")}`;
  const fetching = !!wsIds.length && loadedKey !== fetchKey;
  const hasScores = Object.keys(scoresByWs).length > 0;

  useEffect(() => {
    if (!companyId || !wsIds.length) {
      setScoresByWs({});
      setLoadedKey(fetchKey);
      return undefined;
    }
    let alive = true;
    Promise.all(
      wsIds.map((id) =>
        fetchWorkspaceScores(companyId, id, months)
          .then((s) => [id, s])
          .catch(() => [id, []]),
      ),
    ).then((pairs) => {
      if (!alive) return;
      setScoresByWs(Object.fromEntries(pairs));
      setLoadedKey(fetchKey);
    });
    return () => {
      alive = false;
    };
  }, [companyId, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    const { from, to } = resolveWindow(bucket === "year" ? "ytd" : bucket, custom);

    /* Fold each workspace once — every lookup below reads this map. perUser
       is what turns a workspace's Hours Worked total into one number per
       employee (HCP hours sync writes them by ownerId already — see
       teamHoursKpi.js — so this is the same fold the department figure
       already did, just keeping the per-owner breakdown instead of summing
       it away). */
    const totalsByWs = {};
    const perUserByWs = {};
    wsIds.forEach((id) => {
      const defs = (companyKpis && companyKpis[id]) || [];
      const { perKpi, perUser } = sumScores(scoresByWs[id], from, metaByIdFromDefs(defs), to);
      totalsByWs[id] = perKpi;
      perUserByWs[id] = perUser;
    });

    /* Revenue syncs live from the company's Total Revenue metric when one
       exists (Setup's self-heal keeps revKpi pointed at it) — the typed
       figure is only ever the fallback for a company that hasn't added it. */
    const revenueLive = !!revKpi;
    const revenue = revenueLive
      ? num(totalsByWs[revKpi.workspaceId]?.[revKpi.kpiId]?.amt)
      : typedRevenue;

    const out = rows.map((r) => {
      const memberIds = workspaceIdsOf(r);
      const combined = memberIds.length > 1;
      const goalPct = num(r.goalPct);

      /* Every employee in this department — every workspace the row combines,
         plus any sub-workspaces each one groups. Hours are live wherever that
         member workspace tracks Hours Worked, typed nowhere (there is no
         per-employee typed-hours fallback, only the department-level one
         below). Pay/hour is typed per employee on the card; the department
         figure below is the average of these. A combined row badges every
         employee with their own workspace's name, not just sub-workspaces —
         "Others" lumping together unrelated teams has no other way to tell
         them apart. */
      const pay = employeePayOf(r);
      const memberNodes = memberWsOf(memberIds, includesSubWorkspaces(r));
      /* Whether HOURS ARE A LIVE CONCEPT for this department at all — any ONE
         member (or sub-workspace) tracking Hours Worked is enough. Decided at
         the row level, not per member: a grouped department is a mix of
         member workspaces on purpose (Technicians + a sub-team, say), and a
         member that happens not to have its own Hours Worked metric doesn't
         make the DEPARTMENT a manual one — it just means that member's people
         can never have real hours, so per the row below they never get a
         roster spot. Only a department where NO member anywhere tracks Hours
         Worked (Office, Sales) is treated as fully manual, where the "no
         hours won't show" rule doesn't apply because there is no hours
         concept to apply it to. */
      const anyHoursTracked = memberNodes.some((mw) => !!hoursWorkedKpiOf(companyKpis, mw.id));
      /* Every real employee in this department, hours-filtered or not — split
         below into what the card actually rosters and what it hides, so the
         "+ Add employee" picker (index.jsx) can offer exactly who is missing,
         no more and no less. */
      const roster = memberNodes.flatMap((mw) => {
        const hKpiId = hoursWorkedKpiOf(companyKpis, mw.id);
        let badge = null;
        if (mw.sub) badge = mw.name;
        else if (combined) badge = wsNameById[mw.id] || null;
        return (allUsers || [])
          .filter((u) => String(u?.groupId || "") === mw.id && isRealEmployee(u))
          .map((u) => {
            const uid = String(u.userId || u._id || "");
            const empHours = hKpiId ? perUserByWs[mw.id]?.[uid]?.kpis?.[hKpiId]?.amt : null;
            const entry = pay[uid] ?? null;
            /* A salaried employee with nothing live yet still has SOME hours
               figure worth showing — the same weekday×8 assumption their
               Pay/hour is already built on (monthlyAssumedHours). Kept apart
               from `hours` itself so it can never silently promote a typed
               department to "live" (nodeHours below only ever sums real
               `hours`) — the department stays honestly labelled "typed in"
               even once its Hours total includes these estimates. */
            const isMonthly = payEntryMode(entry) === "monthly";
            const hoursEstimated = empHours == null && isMonthly ? monthlyAssumedHours(months) : null;
            return {
              userId: uid,
              name: employeeName(u),
              subWorkspace: badge,
              workspaceId: mw.id,
              hours: empHours == null ? null : num(empHours),
              hoursEstimated,
              payPerHour: hourlyRateOf(entry, months),
              payEntry: entry,
            };
          });
      });
      /* A department where hours are live anywhere only rosters whoever
         actually has hours this period — active or archived, so a tech who
         got archived mid-period still shows up (and still counts below)
         instead of silently dropping off the moment they're deactivated — OR
         is already on a monthly rate, which is owed for the window whether or
         not their hours have synced yet (that's the whole point of a salary
         instead of an hourly rate: it never reads as "no pace" the way a
         zero-hours row would). Someone with neither hours nor a rate is left
         off rather than cluttering the list with a permanent "—" — the "+ Add
         employee" picker in index.jsx is how a brand-new hire gets a monthly
         rate typed for the first time, before they have any hours to show. A
         fully manual department (Office, Sales) has no hours concept to
         filter on at all — every real employee there keeps a row so a rate
         can still be typed by hand. */
      const isVisible = (e) => !anyHoursTracked
        || (e.hours != null && e.hours > 0)
        || payEntryMode(e.payEntry) === "monthly";
      const employees = roster.filter(isVisible).sort((a, b) => a.name.localeCompare(b.name));
      const hiddenEmployees = roster.filter((e) => !isVisible(e)).sort((a, b) => a.name.localeCompare(b.name));

      /* Pay/hour (display) blends every VISIBLE hourly-equivalent rate, same
         as always. Monthly employees' own cost is worked out separately below
         (monthlyCostOf) rather than through this average, so a salary is
         never multiplied against hours at all. */
      const paidRates = employees.map((e) => e.payPerHour).filter((v) => v != null);
      const avgPay = paidRates.length ? paidRates.reduce((s, v) => s + v, 0) / paidRates.length : null;
      /* Every roster member's real monthly cost, visible or not — a salary is
         owed for the window regardless of whether hours have synced, so it
         must never depend on the "no hours won't show" filter above. */
      const monthlyCostTotal = roster
        .map((e) => monthlyCostOf(e.payEntry, months))
        .filter((v) => v != null)
        .reduce((s, v) => s + v, 0);
      /* Hours that feed the blended hourly cost below — every monthly
         employee is excluded, so their hours (if any have synced) are never
         multiplied against the hourly average on top of their own flat
         salary already counted in monthlyCostTotal. */
      const hourlyHours = employees
        .filter((e) => payEntryMode(e.payEntry) !== "monthly")
        .map((e) => e.hours)
        .filter((v) => v != null)
        .reduce((s, v) => s + v, 0);

      /* Sum each member workspace's hours from its OWN roster above rather
         than the workspace's raw Hours Worked total — the roster is now the
         MORE inclusive of the two (it keeps an archived tech's hours; the
         filter above only ever drops someone with zero/no hours to show), so
         summing it is never a loss and it's also what the card's own expanded
         list adds up to, so the two numbers always reconcile. The raw
         per-workspace total is kept ONLY as a fallback for a member that
         tracks Hours Worked but has no roster rows at all this period
         (nothing to sum, so there is nothing to prefer over it) — that
         safety net is what keeps a sync gap from ever reading as a silent
         zero. Working out each member node's own contribution separately —
         rather than one flag for "any member is live" — is what keeps a
         Technicians + Office combined row from double-counting Technicians'
         hours once as its own workspace total and again as the same
         employees' individual figures. */
      const nodeHours = memberNodes.map((mw) => {
        const ownEmpHours = employees
          .filter((e) => e.workspaceId === mw.id)
          .map((e) => e.hours)
          .filter((v) => v != null);
        if (ownEmpHours.length) return { live: true, amt: ownEmpHours.reduce((s, v) => s + v, 0) };
        const hk = hoursWorkedKpiOf(companyKpis, mw.id);
        if (hk) return { live: true, amt: num(totalsByWs[mw.id]?.[hk]?.amt) };
        return { live: false, amt: 0 };
      });
      const live = nodeHours.some((n) => n.live);
      /* Salaried employees' estimated hours (above) are added on top of
         whichever base the department already had — live-summed or typed —
         rather than folded into nodeHours itself, so a department's `live`
         flag keeps meaning "has a real Hours Worked feed somewhere" and
         nothing here can flip a typed department "live" just because someone
         on it draws a salary. */
      const monthlyEstimatedHours = employees.reduce((s, e) => s + (e.hoursEstimated || 0), 0);
      const hours = (live ? nodeHours.reduce((s, n) => s + n.amt, 0) : num(r.hours)) + monthlyEstimatedHours;

      /* Total cost is worked out, not typed — the hourly portion (hours ×
         the department's blended pay/hour) plus every monthly salary owed
         for the window, added on top rather than folded into the same
         multiplication. A typed total cost only remains the fallback for a
         department with NEITHER an hourly rate NOR a monthly one entered yet
         (nothing real to add up) — a department that has picked up its first
         monthly employee no longer falls back to that typed figure, because
         there is now a real number to show instead. */
      const hourlyCost = avgPay != null ? hourlyHours * avgPay : 0;
      const typedFallback = avgPay == null && monthlyCostTotal === 0;
      const cost = typedFallback ? num(r.totalCost) : hourlyCost + monthlyCostTotal;
      const goalAmt = revenue * (goalPct / 100);
      /* No revenue typed yet leaves performance undefined rather than 0% —
         0% would read as "this department costs nothing". */
      const perfPct = revenue > 0 ? (cost / revenue) * 100 : null;

      return {
        ...r,
        live,
        hours,
        cost,
        goalPct,
        goalAmt,
        perfPct,
        employees,
        hiddenEmployees,
        payPerHour: avgPay,
        deltaPct: perfPct == null ? null : goalPct - perfPct,
        deltaAmt: revenue > 0 ? cost - goalAmt : null,
        over: revenue > 0 && cost > goalAmt,
      };
    });

    const totalCost = out.reduce((sum, r) => sum + r.cost, 0);
    const totalHours = out.reduce((sum, r) => sum + r.hours, 0);
    const totalGoalPct = out.reduce((sum, r) => sum + r.goalPct, 0);
    const totalGoalAmt = revenue * (totalGoalPct / 100);

    return {
      rows: out,
      revenue,
      revenueLive,
      totalCost,
      totalHours,
      totalGoalPct,
      totalGoalAmt,
      payrollPerfPct: revenue > 0 ? (totalCost / revenue) * 100 : null,
      payrollDeltaAmt: revenue > 0 ? totalCost - totalGoalAmt : null,
      payrollOver: revenue > 0 && totalCost > totalGoalAmt,
      loading: fetching && !hasScores,
      refreshing: fetching && hasScores,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(rows),
    typedRevenue,
    revKpi?.workspaceId,
    revKpi?.kpiId,
    scoresByWs,
    companyKpis,
    bucket,
    customKey,
    fetching,
    hasScores,
    allUsers,
    subWsByParent,
    wsNameById,
  ]);
}
